// src/pages/Categories/index.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../contexts/LanguageContext";
import { fetchCategories, deleteCategory } from "../../redux/slices/categoriesSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import NewCategoryModal from "../../components/Categories/NewCategoryModal";
import EditCategoryModal from "../../components/Categories/EditCategoryModal";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import placeholderImage from "../../assets/images/placeholder.jpg";
import { 
  MagnifyingGlassIcon, 
  ChevronDownIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from "@heroicons/react/24/outline";

function Categories() {
  const dispatch = useDispatch();
  const { t, tStatus } = useTranslation();
  const { categories = [], loading, error } = useSelector((state) => state.categories || {});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Option bar state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortBy, setSortBy] = useState("Priority");

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const toggleMenu = (id) => {
    setOpenMenu(openMenu === id ? null : id);
  };

  const openDeleteModal = (categoryId) => {
    setCategoryToDelete(categoryId);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = () => {
    if (!categoryToDelete) return;
    
    setIsDeleting(true);
    dispatch(deleteCategory(categoryToDelete))
      .unwrap()
      .then(() => {
        dispatch(showNotification({ message: "Category deleted successfully!", type: "success" }));
        setIsDeleteModalOpen(false);
        setCategoryToDelete(null);
      })
      .catch((error) => {
        dispatch(showNotification({ 
          message: error || "Failed to delete category", 
          type: "error" 
        }));
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const openEditModal = (category) => {
    setCategoryToEdit(category);
    setIsEditModalOpen(true);
  };

  // Handle image error by setting placeholder
  const handleImageError = (e) => {
    e.target.src = placeholderImage;
  };

  // Get image source with fallback to placeholder
  const getImageSrc = (categoryImage) => {
    return categoryImage?.trim() ? categoryImage : placeholderImage;
  };

  // Process categories based on search, filter, sort
  const parentCategories = categories.filter((cat) => !cat.parentId);

  const filteredCategories = parentCategories.filter((cat) => {
    if (searchTerm && !cat.categoryName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filterStatus === "Active" && !cat.isActive) return false;
    if (filterStatus === "Closed" && cat.isActive) return false;
    return true;
  });

  const sortedCategories = filteredCategories.sort((a, b) => {
    if (sortBy === "Name") {
      return a.categoryName.localeCompare(b.categoryName);
    }
    // Default: sort by priority (ascending)
    return a.priorityOrder - b.priorityOrder;
  });

  return (
    <div>
      {/* Table Container */}
      <div className="mt-4 bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
        {/* Dark Header */}
        <div className="bg-dashboardDark h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-white font-semibold text-lg">{t("categories.title")}</h2>
            <span className="text-white font-semibold">({sortedCategories.length})</span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 border border-white rounded">
              <MagnifyingGlassIcon className="w-[18px] h-[18px] text-white" />
              <input
                type="text"
                placeholder={t("common.search")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent text-white placeholder-white/70 text-sm font-light outline-none w-28"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-transparent border border-white rounded px-3 py-1.5 text-white text-sm font-light outline-none cursor-pointer"
            >
              <option value="All" className="text-dashboardDark">{t("categories.filter.allStatus")}</option>
              <option value="Active" className="text-dashboardDark">{t("categories.filter.active")}</option>
              <option value="Closed" className="text-dashboardDark">{t("categories.filter.closed")}</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent border border-white rounded px-3 py-1.5 text-white text-sm font-light outline-none cursor-pointer"
            >
              <option value="Priority" className="text-dashboardDark">{t("categories.sort.priority")}</option>
              <option value="Name" className="text-dashboardDark">{t("categories.sort.name")}</option>
            </select>
            
            <button 
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-4 py-1.5 bg-dashboardCoral rounded text-white text-sm font-medium"
            >
              <PlusIcon className="w-[18px] h-[18px]" />
              <span>{t("common.addNew")}</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dashboardCoral"></div>
            <span className="ml-2">{t("categories.loading")}</span>
          </div>
        ) : error ? (
          <p className="p-4 text-red-500">Error: {error}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dashboardStroke">
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.image")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.name")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("categories.col.priority")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.status")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.createdAt")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.updatedAt")}</th>
                  <th className="px-4 py-4 text-left text-[17px] font-medium text-dashboardText">{t("col.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedCategories.map((category, index) => (
                  <React.Fragment key={category._id}>
                    <tr 
                      onClick={() => toggleMenu(category._id)} 
                      className={`border-b border-dashboardStroke cursor-pointer hover:bg-gray-50 transition-colors ${
                        index === 0 ? "shadow-[inset_3px_0px_0px_#FF7C17]" : ""
                      }`}
                    >
                      <td className="px-4 py-4">
                        <img
                          src={getImageSrc(category.categoryImage)}
                          alt={category.categoryName}
                          className="h-10 w-10 object-cover rounded-full"
                          onError={handleImageError}
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {category.categoryName}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {category.priorityOrder}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                          category.status === 'active' ? 'bg-green-100 text-green-800' : 
                          category.status === 'deleted' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                            category.status === 'active' ? 'bg-green-500' : 
                            category.status === 'deleted' ? 'bg-red-500' : 'bg-gray-400'
                          }`} />
                          {tStatus(category.status)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {new Date(category.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                        {new Date(category.updatedAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-0.5">
                          <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                            <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                          </button>
                          <div className="w-px h-6 bg-dashboardStroke"></div>
                          <button 
                            onClick={(e) => { e.stopPropagation(); openEditModal(category); }}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            <PencilIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                          </button>
                          <div className="w-px h-6 bg-dashboardStroke"></div>
                          <button
                            onClick={(e) => { e.stopPropagation(); openDeleteModal(category._id); }}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            <TrashIcon className="w-6 h-6 text-dashboardCoral" strokeWidth={1.3} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {openMenu === category._id &&
                      [...(category.subcategories || [])]
                        .sort((a, b) => a.priorityOrder - b.priorityOrder)
                        .map((subcat) => (
                          <tr key={subcat._id} className="border-b border-dashboardStroke bg-gray-50/50">
                            <td className="px-4 py-4 pl-8">
                              <img
                                src={getImageSrc(subcat.categoryImage)}
                                alt={subcat.categoryName}
                                className="h-8 w-8 object-cover rounded-full"
                                onError={handleImageError}
                              />
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                              <span className="text-gray-400">--</span> {subcat.categoryName}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                              {subcat.priorityOrder}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                                subcat.status === 'active' ? 'bg-green-100 text-green-800' : 
                                subcat.status === 'deleted' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                                  subcat.status === 'active' ? 'bg-green-500' : 
                                  subcat.status === 'deleted' ? 'bg-red-500' : 'bg-gray-400'
                                }`} />
                                {tStatus(subcat.status)}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                              {new Date(subcat.createdAt).toLocaleString()}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                              {new Date(subcat.updatedAt).toLocaleString()}
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-0.5">
                                <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                                  <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                                </button>
                                <div className="w-px h-6 bg-dashboardStroke"></div>
                                <button 
                                  onClick={() => openEditModal(subcat)}
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                >
                                  <PencilIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                                </button>
                                <div className="w-px h-6 bg-dashboardStroke"></div>
                                <button
                                  onClick={() => openDeleteModal(subcat._id)}
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                >
                                  <TrashIcon className="w-6 h-6 text-dashboardCoral" strokeWidth={1.3} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                    }
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Category Modal */}
      {isAddModalOpen && (
        <NewCategoryModal
          isOpen={isAddModalOpen}
          setIsOpen={setIsAddModalOpen}
          categories={categories.filter((cat) => !cat.parentId)}
        />
      )}

      {/* Edit Category Modal */}
      {categoryToEdit && (
        <EditCategoryModal
          isOpen={isEditModalOpen}
          setIsOpen={setIsEditModalOpen}
          category={categoryToEdit}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={t("deleteConfirm.category.title")}
        message={t("deleteConfirm.category.message")}
        confirmText={t("deleteConfirm.delete")}
        cancelText={t("deleteConfirm.cancel")}
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
}

export default Categories;
